import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw, MessageCircle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errorService } from "@/lib/errorService";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  reportSent: boolean;
  reportSending: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      reportSent: false,
      reportSending: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("App error caught by ErrorBoundary:", error, errorInfo);
    console.error("ErrorBoundary ERROR MESSAGE:", error?.message);
    console.error("ErrorBoundary ERROR STACK:", error?.stack);
    console.error("ErrorBoundary ERROR NAME:", error?.name);
    this.setState({ errorInfo });
    
    errorService.logError(error, {
      severity: 'high',
      componentName: 'ErrorBoundary',
      route: window.location.pathname,
    });
  }

  handleTryAgain = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      reportSent: false,
      reportSending: false
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleReportIssue = async () => {
    if (this.state.reportSending || this.state.reportSent) return;
    
    this.setState({ reportSending: true });
    
    try {
      await errorService.logError(this.state.error || new Error("Unknown error"), {
        severity: 'high',
        componentName: 'ErrorBoundary-UserReport',
        route: window.location.pathname,
        additionalInfo: 'User manually reported this issue'
      });
      this.setState({ reportSent: true, reportSending: false });
    } catch (e) {
      console.error("Failed to send report:", e);
      this.setState({ reportSending: false });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground">
                We're sorry, but something unexpected happened. You can try again or refresh the page.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                onClick={this.handleTryAgain}
                className="gap-2 w-full"
                data-testid="button-try-again"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={this.handleRefresh}
                  className="flex-1"
                  data-testid="button-refresh-page"
                >
                  Refresh Page
                </Button>
                <Button 
                  variant="outline" 
                  onClick={this.handleGoHome}
                  className="flex-1"
                  data-testid="button-go-home"
                >
                  Go to Home
                </Button>
              </div>
              
              <Button
                variant="ghost"
                onClick={this.handleReportIssue}
                disabled={this.state.reportSending || this.state.reportSent}
                className="gap-2 text-muted-foreground"
                data-testid="button-report-issue"
              >
                {this.state.reportSent ? (
                  <>
                    <Check className="w-4 h-4 text-green-500" />
                    Report Sent
                  </>
                ) : this.state.reportSending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4" />
                    Report Issue
                  </>
                )}
              </Button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Technical Details
                </summary>
                <pre className="mt-2 p-4 bg-muted rounded-lg text-xs overflow-auto max-h-48 text-destructive">
                  {this.state.error.message}
                  {"\n\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
